// QRCodePanel.jsx
// Staff-facing panel — requires restaurant_token in localStorage
// Usage in Tables.jsx:
//   import QRCodePanel from './QRCodePanel';
//   {showQR && <QRCodePanel table={selectedTable} onClose={() => setShowQR(false)} />}
//
// ─────────────────────────────────────────────────────────────
//  IMPORTANT — qr_url uses /menu-list?token= (not /menu?token=)
//
//  Your backend's POST /qr/generate/:tableId must return:
//    {
//      qr_token:     "abc123xyz",
//      qr_url:       "https://yourapp.com/menu-list?token=abc123xyz",
//      qr_image_url: "https://api.qrserver.com/v1/create-qr-code/?data=..."
//    }
//
//  If your backend auto-generates qr_url, make sure it builds it as:
//    const qr_url = `${process.env.FRONTEND_URL}/menu-list?token=${qr_token}`;
//                                                   ^^^^^^^^^^^
//                                                   changed from /menu → /menu-list
//
//  If your backend just stores the token and the frontend builds the URL,
//  this component already constructs it correctly (see handleGenerate below).
// ─────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { baseurl, frontendurl } from '../../baseurl'; // add frontendurl export if not present
import {
  X, QrCode, Download, Printer, RefreshCw, Loader2,
  Copy, CheckCircle2, ExternalLink, AlertTriangle,
  Eye, EyeOff, BarChart2, Trash2
} from 'lucide-react';

const api = () => {
  const token = localStorage.getItem('restaurant_token');
  return axios.create({ baseURL: baseurl, headers: { Authorization: `Bearer ${token}` } });
};

// The public route customers land on after scanning
// Must match the route you added in App.jsx / routes.jsx:
//   <Route path="/menu-list" element={<CustomerMenu />} />
const MENU_PATH = '/menu-list';

export default function QRCodePanel({ table, onClose }) {
  const [qr,           setQr]           = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [generating,   setGenerating]   = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [copied,       setCopied]       = useState(false);
  const [showUrl,      setShowUrl]      = useState(false);

  useEffect(() => { fetchQR(); }, [table.id]);

  const fetchQR = async () => {
    setLoading(true);
    try {
      const r = await api().get(`/qr/table/${table.id}`);
      // Normalise: if backend returns qr_url, use it.
      // If not, build it from qr_token using MENU_PATH.
      const raw = r.data.qr || null;
      if (raw && raw.qr_token && !raw.qr_url) {
        raw.qr_url = buildMenuUrl(raw.qr_token);
      }
      setQr(raw);
    } catch { setQr(null); }
    finally  { setLoading(false); }
  };

  // Build the customer-facing URL: /menu-list?token=<qr_token>
  const buildMenuUrl = (token) => {
    const base = (typeof frontendurl !== 'undefined' && frontendurl)
      ? frontendurl.replace(/\/$/, '')
      : window.location.origin;
    return `${base}${MENU_PATH}?token=${token}`;
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const r = await api().post(`/qr/generate/${table.id}`);
      // Use qr_url from backend if provided; otherwise build it here
      const qr_url = r.data.qr_url || buildMenuUrl(r.data.qr_token);
      setQr({
        qr_token:     r.data.qr_token,
        qr_url,
        qr_image_url: r.data.qr_image_url || buildQRImageUrl(qr_url),
        scans:        0,
      });
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to generate QR');
    } finally { setGenerating(false); }
  };

  // Fallback: generate QR image via free public API if backend doesn't provide one
  const buildQRImageUrl = (data) =>
    `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(data)}`;

  const handleDeactivate = async () => {
    if (!window.confirm('Deactivate this QR code? Existing printed QRs will stop working.')) return;
    setDeactivating(true);
    try {
      await api().delete(`/qr/table/${table.id}`);
      setQr(null);
    } catch { alert('Failed to deactivate'); }
    finally  { setDeactivating(false); }
  };

  const handleCopy = async () => {
    if (!qr?.qr_url) return;
    try {
      await navigator.clipboard.writeText(qr.qr_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { alert('Copy failed — select the URL manually'); }
  };

  const handleDownload = () => {
    if (!qr?.qr_image_url) return;
    const a    = document.createElement('a');
    a.href     = qr.qr_image_url;
    a.download = `QR-Table-${table.label}.png`;
    a.target   = '_blank';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handlePrint = () => {
    if (!qr) return;
    const html = `<!DOCTYPE html><html><head><title>QR Code — Table ${table.label}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:-apple-system,sans-serif;background:#fff;padding:16px;}
      .card{border:2px dashed #ddd;border-radius:18px;padding:28px 32px;text-align:center;max-width:320px;width:100%;}
      .logo{font-size:26px;font-weight:900;color:#ff8c00;margin-bottom:4px;}
      .sub{color:#8c7355;font-size:13px;margin-bottom:14px;}
      img{width:220px;height:220px;border-radius:10px;border:1px solid #eee;}
      h2{font-size:26px;font-weight:900;margin:12px 0 4px;color:#1a1208;}
      p{color:#8c7355;font-size:13px;margin-top:4px;line-height:1.5;}
      .url{font-size:8px;color:#c4b49e;margin-top:12px;word-break:break-all;}
    </style></head><body>
    <div class="card">
      <div class="logo">📱 Scan to Order</div>
      <div class="sub">View menu and place your order</div>
      <img src="${qr.qr_image_url}" alt="QR Code"/>
      <h2>Table ${table.label}</h2>
      <p>Scan with your phone camera to see our menu and order directly</p>
      <div class="url">${qr.qr_url}</div>
    </div>
    <script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();}<\/script>
    </body></html>`;
    const w = window.open('', '_blank', 'width=420,height=650');
    if (w) { w.document.write(html); w.document.close(); }
  };

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.panel} onClick={e => e.stopPropagation()}>

        <div style={S.header}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={S.headerIcon}><QrCode size={18} style={{ color:'#ff8c00' }}/></div>
            <div>
              <h3 style={S.headerTitle}>QR Code · Table {table.label}</h3>
              <p style={S.headerSub}>{table.zone_name || 'Floor'} · Cap. {table.capacity}</p>
            </div>
          </div>
          <button onClick={onClose} style={S.closeBtn}><X size={15}/></button>
        </div>

        <div style={S.body}>
          {loading ? (
            <div style={S.center}><Loader2 size={28} style={{ animation:'spin 0.7s linear infinite', color:'#ff8c00' }}/></div>
          ) : qr ? (
            <>
              <div style={S.qrWrapper}>
                <img src={qr.qr_image_url} alt="QR" style={S.qrImg}/>
                {qr.scans > 0 && (
                  <div style={S.scansBadge}><BarChart2 size={10}/> {qr.scans} scan{qr.scans !== 1 ? 's' : ''}</div>
                )}
              </div>

              {/* Route info badge */}
              <div style={{ textAlign:'center', marginBottom:10 }}>
                <span style={{ fontSize:9, fontWeight:700, background:'rgba(99,102,241,0.10)', color:'#6366f1', padding:'3px 10px', borderRadius:100 }}>
                  /menu-list?token=•••
                </span>
              </div>

              <p style={S.hint}>Customers scan this with their phone camera to view the menu and place orders directly to this table.</p>

              <div style={S.urlRow}>
                <span style={S.urlText}>{showUrl ? qr.qr_url : qr.qr_url.replace(/token=.*/, 'token=••••••')}</span>
                <button onClick={() => setShowUrl(v => !v)} style={S.iconBtn}>{showUrl ? <EyeOff size={13}/> : <Eye size={13}/>}</button>
                <button onClick={handleCopy} style={{ ...S.iconBtn, color: copied ? '#22c55e' : '#8c7355' }}>
                  {copied ? <CheckCircle2 size={13}/> : <Copy size={13}/>}
                </button>
                <a href={qr.qr_url} target="_blank" rel="noreferrer" style={{ ...S.iconBtn, display:'flex', color:'#8c7355', textDecoration:'none' }}>
                  <ExternalLink size={13}/>
                </a>
              </div>

              <div style={S.actionsGrid}>
                <button onClick={handleDownload}  style={S.actionBtn}><Download size={13}/> Download</button>
                <button onClick={handlePrint}     style={S.actionBtnPrimary}><Printer size={13}/> Print QR</button>
              </div>

              <div style={{ display:'flex', gap:8, marginTop:8 }}>
                <button onClick={handleGenerate} disabled={generating} style={{ ...S.actionBtn, flex:1 }}>
                  {generating ? <Loader2 size={12} style={{ animation:'spin 0.7s linear infinite' }}/> : <RefreshCw size={12}/>} Regenerate
                </button>
                <button onClick={handleDeactivate} disabled={deactivating} style={S.dangerBtn}>
                  {deactivating ? <Loader2 size={12} style={{ animation:'spin 0.7s linear infinite' }}/> : <Trash2 size={12}/>} Deactivate
                </button>
              </div>

              <div style={S.warnBox}>
                <AlertTriangle size={11} style={{ flexShrink:0 }}/>
                <span>Regenerating creates a new QR token. Old printed QR codes will stop working.</span>
              </div>
            </>
          ) : (
            <div style={{ textAlign:'center', padding:'20px 0' }}>
              <div style={{ fontSize:48, marginBottom:12 }}>🔲</div>
              <h4 style={{ fontSize:14, fontWeight:800, color:'#1a1208', marginBottom:6 }}>No QR Code Yet</h4>
              <p style={{ fontSize:12, color:'#8c7355', marginBottom:16 }}>
                Generate a QR code for Table {table.label}.<br/>
                Customers scan it to open the menu at<br/>
                <code style={{ fontSize:10, color:'#6366f1' }}>/menu-list?token=…</code>
              </p>
              <button onClick={handleGenerate} disabled={generating} style={{ ...S.actionBtnPrimary, width:'100%', padding:'12px' }}>
                {generating
                  ? <><Loader2 size={14} style={{ animation:'spin 0.7s linear infinite' }}/> Generating…</>
                  : <><QrCode size={14}/> Generate QR Code</>}
              </button>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
    </div>
  );
}

const S = {
  overlay:          { position:'fixed', inset:0, zIndex:1300, background:'rgba(0,0,0,0.45)', backdropFilter:'blur(5px)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 },
  panel:            { background:'#fff', borderRadius:20, width:'100%', maxWidth:340, boxShadow:'0 28px 70px rgba(0,0,0,0.20)', display:'flex', flexDirection:'column', overflow:'hidden' },
  header:           { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 16px', borderBottom:'1px solid rgba(0,0,0,0.07)' },
  headerIcon:       { width:36, height:36, borderRadius:10, background:'rgba(255,140,0,0.10)', display:'flex', alignItems:'center', justifyContent:'center' },
  headerTitle:      { fontSize:13, fontWeight:800, color:'#1a1208' },
  headerSub:        { fontSize:11, color:'#8c7355' },
  closeBtn:         { border:'none', background:'rgba(0,0,0,0.07)', borderRadius:8, padding:6, cursor:'pointer', display:'flex' },
  body:             { padding:'16px', overflowY:'auto', maxHeight:'78vh' },
  center:           { display:'flex', alignItems:'center', justifyContent:'center', padding:32 },
  qrWrapper:        { display:'flex', justifyContent:'center', position:'relative', background:'#f9f5f0', borderRadius:14, padding:16, marginBottom:8 },
  qrImg:            { width:220, height:220, borderRadius:10 },
  scansBadge:       { position:'absolute', top:10, right:10, background:'rgba(255,140,0,0.90)', color:'#fff', fontSize:9, fontWeight:700, padding:'3px 8px', borderRadius:100, display:'flex', alignItems:'center', gap:3 },
  hint:             { fontSize:11, color:'#8c7355', textAlign:'center', lineHeight:1.5, marginBottom:12 },
  urlRow:           { display:'flex', alignItems:'center', gap:5, background:'rgba(0,0,0,0.04)', borderRadius:9, padding:'7px 10px', marginBottom:12 },
  urlText:          { fontSize:9.5, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', color:'#8c7355' },
  iconBtn:          { border:'none', background:'none', cursor:'pointer', display:'flex', padding:3, color:'#8c7355', flexShrink:0 },
  actionsGrid:      { display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 },
  actionBtn:        { display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'9px', borderRadius:9, border:'1px solid rgba(0,0,0,0.10)', background:'#fff', cursor:'pointer', fontSize:11, fontWeight:700, color:'#1a1208' },
  actionBtnPrimary: { display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'9px', borderRadius:9, border:'none', background:'linear-gradient(135deg,#ff8c00,#ffbf00)', color:'#fff', cursor:'pointer', fontSize:11, fontWeight:700 },
  dangerBtn:        { display:'flex', alignItems:'center', justifyContent:'center', gap:5, padding:'9px 12px', borderRadius:9, border:'1px solid rgba(239,68,68,0.25)', background:'rgba(239,68,68,0.06)', color:'#ef4444', cursor:'pointer', fontSize:11, fontWeight:700 },
  warnBox:          { display:'flex', alignItems:'flex-start', gap:6, background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.20)', borderRadius:8, padding:'8px 10px', marginTop:8, fontSize:10, color:'#92400e', lineHeight:1.4 },
};
